import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import useSeo from '@/hooks/useSeo';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dah84gboh653ivfvnno0';

const guides = [
  { icon: 'ri-map-pin-time-line', title: '写清楚时间和地点', text: '哪怕是模糊的“本周三晚上”，也比完全没有时间信息更有参考价值。' },
  { icon: 'ri-file-copy-2-line', title: '描述你亲眼看到或听到的', text: '尽量区分“我看到的”和“我听别人说的”，方便我们判断可信度。' },
  { icon: 'ri-shield-keyhole-line', title: '来源信息会严格保密', text: '如果你希望匿名，请在详情里注明，我们不会公开任何可识别信息。' },
];

export default function SubmitPage() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const crumbs = [{ label: '首页', to: '/' }, { label: '我要爆料' }];

  useSeo({
    title: '我要爆料 - 提交线索给爆料吃瓜编辑团队',
    description:
      '在爆料吃瓜提交你的现场线索。填写时间、地点与事件概述，编辑会在核实后整理成文。所有来源信息全程保密，支持匿名投稿。',
    keywords: '爆料吃瓜,我要爆料,爆料投稿,爆料吃瓜现场',
    path: '/submit',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: '我要爆料 - 爆料吃瓜',
        inLanguage: 'zh-CN',
        url: 'https://rudolf1922.cn/submit',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: crumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.label,
          item: crumb.to ? `https://rudolf1922.cn${crumb.to}` : undefined,
        })),
      },
    ],
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const raw = new FormData(formEl);

    const honeypot = String(raw.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }

    const params = new URLSearchParams();
    raw.forEach((value, key) => {
      if (key !== 'website_alt') params.append(key, String(value));
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const lowerMsg = String(serverMsg).toLowerCase();
      const isSpam = lowerMsg.includes('spam') || lowerMsg.includes('form data is spam');
      const isOk = response.ok && parsed?.code === 'OK' && !isSpam;

      if (isOk) {
        setStatus('success');
        formEl.reset();
      } else {
        setStatus('error');
        setFormError(
          serverMsg && !isSpam
            ? String(serverMsg)
            : '提交未能成功，请检查填写内容后重试。',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络连接异常，请稍后重试。');
    }
  };

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="w-full px-4 py-8 md:px-6 md:py-12">
          <Breadcrumb items={crumbs} />
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-5xl">
            我要爆料
          </h1>
          <p className="mt-4 max-w-3xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
            把你看到的、听到的告诉我们。编辑会先做交叉核实，再决定是否整理成文。你可以选择匿名，所有来源信息都会严格保密。
          </p>
        </div>
      </div>

      <div className="w-full px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {status === 'success' ? (
              <div className="rounded-lg border border-secondary-300 bg-secondary-50 p-8 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                  <i className="ri-check-line text-3xl"></i>
                </span>
                <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950">线索已收到，谢谢你的分享</h2>
                <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600">
                  编辑团队会尽快核实这条线索。如果你的信息足够完整，我们可能会通过你留下的邮箱进一步联系。
                </p>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    再提交一条
                  </button>
                  <Link
                    to="/category/all"
                    className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 font-label text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                  >
                    去看看最新爆料
                  </Link>
                </div>
              </div>
            ) : (
              <form
                id="baoliao-submit-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
              >
                <h2 className="font-heading text-xl font-bold text-foreground-950">线索信息</h2>
                <p className="mt-2 font-body text-sm text-foreground-600">
                  带 <span className="text-primary-600">*</span> 的是必填项，其余可以留空。
                </p>

                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="nickname" className="font-label text-sm font-medium text-foreground-800">
                      你的称呼 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="nickname"
                      name="nickname"
                      type="text"
                      required
                      placeholder="可以是昵称或化名"
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-label text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="font-label text-sm font-medium text-foreground-800">
                      联系邮箱 <span className="text-primary-600">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于编辑向你核实细节"
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-label text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    />
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="tip_type" className="font-label text-sm font-medium text-foreground-800">
                      线索类型
                    </label>
                    <select
                      id="tip_type"
                      name="tip_type"
                      defaultValue="star"
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-label text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    >
                      <option value="star">明星动态</option>
                      <option value="influencer">网红圈</option>
                      <option value="screen">影视综艺</option>
                      <option value="life">情感生活</option>
                      <option value="business">带货打假</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="evidence" className="font-label text-sm font-medium text-foreground-800">
                      是否可提供证据
                    </label>
                    <select
                      id="evidence"
                      name="evidence"
                      defaultValue="photo"
                      className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-label text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                    >
                      <option value="photo">有照片或截图</option>
                      <option value="video">有视频素材</option>
                      <option value="text">仅有文字描述</option>
                      <option value="later">后续可补充</option>
                    </select>
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="subject" className="font-label text-sm font-medium text-foreground-800">
                    涉及人物或事件
                  </label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    placeholder="例如：某综艺录制现场的阵容变化"
                    className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-label text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  />
                </div>

                <div className="mt-5">
                  <label htmlFor="detail" className="font-label text-sm font-medium text-foreground-800">
                    线索详情 <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="detail"
                    name="detail"
                    required
                    maxLength={500}
                    rows={6}
                    placeholder="请描述时间、地点、你看到或听到的内容，以及你判断可信度的依据（不超过 500 字）"
                    className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 font-label text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
                  ></textarea>
                  <p className="mt-1.5 font-label text-xs text-foreground-500">最多 500 字，请尽量描述具体。</p>
                </div>

                <div className="mt-5 flex items-start gap-2">
                  <input
                    id="anonymous"
                    name="anonymous"
                    type="checkbox"
                    value="yes"
                    className="mt-1 h-4 w-4 rounded border-background-300 text-primary-500 focus:ring-primary-300"
                  />
                  <label htmlFor="anonymous" className="font-label text-sm text-foreground-700">
                    希望匿名处理，不公开任何可识别我身份的信息
                  </label>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="mailbox-note"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === 'error' && formError && (
                  <div className="mt-6 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 font-label text-sm text-primary-700">
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="mt-6 inline-flex w-full items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  <i className="ri-send-plane-line mr-2 text-base"></i>
                  {status === 'submitting' ? '正在提交...' : '提交线索'}
                </button>
                <p className="mt-3 font-label text-xs text-foreground-500">
                  提交即表示你确认所提供内容为真实见闻，我们会对来源信息严格保密。
                </p>
              </form>
            )}
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">爆料小贴士</h2>
              <ul className="mt-4 space-y-4">
                {guides.map((item) => (
                  <li key={item.title} className="flex gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                      <i className={`${item.icon} text-lg`}></i>
                    </span>
                    <div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-950">{item.title}</h3>
                      <p className="mt-1 font-body text-sm leading-relaxed text-foreground-600">{item.text}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">其他联系方式</h2>
              <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600">
                不方便填表？也可以直接发邮件给我们，说明线索概述即可。
              </p>
              <a
                href="mailto:baoliao@chigua-site.com"
                rel="nofollow"
                className="mt-4 inline-flex items-center gap-2 font-label text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
              >
                <i className="ri-mail-send-line text-base"></i>
                baoliao@chigua-site.com
              </a>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}